import React, { useEffect, useRef, useState } from 'react';
import { NodeLink } from './index';
import { NonIdealState } from '@blueprintjs/core';
import * as d3 from 'd3';
import { DefaultChartColorScales } from 'styles/chartColors';

interface Node {
	label: string;
}

interface Link {
	key: string;
}

/**
 * ChartWrapper for the physics based clustering diagram.
 *
 * Expects: [{count, labels:['term'...]}]
 * Converts: [nodes: [], links: [{source, target}]]
 *
 * @param getColorScale
 * @param dimensions
 * @param data
 * @param endpointQuery
 * @param widgetConfiguration
 * @returns {*}
 * @constructor
 */
export function NodeLinkWrapper({ getColorScale, dimensions, data, endpointQuery, widgetConfiguration }) {
	const [convertedData, setConvertedData] = useState({ nodes: [], links: [] });
	const nodeDom = useRef(d3.create('div'));
	const linkDom = useRef(d3.create('div'));

	useEffect(() => {
		// empty data is "valid" and different than null.
		if (data?.length === 0) {
			setConvertedData({ nodes: [], links: [] });
		}

		// the data is null, or in the wrong shape...so we exit
		if (!data || !data?.nodes) return;

		const existingNodes = {};
		nodeDom.current
			.selectAll('div')
			.data()
			.forEach((d: Node) => (existingNodes[d.label] = d));

		const nodes = {};
		const links = {};

		// for each entry, create a node, keys are the labels
		data.nodes.forEach(({ label, connections, metadata }) => {
			let sizeValue;
			const mdSizeKey = endpointQuery?.meta?.['Size Nodes By'];
			const mdSizeValue = metadata?.[mdSizeKey];

			// there are three choices for sizing: none, size by connections, or by property
			if (widgetConfiguration?.sizeByConnection) {
				sizeValue = connections;
			} else if (!widgetConfiguration?.sizeByConnection && mdSizeKey && mdSizeValue) {
				sizeValue = mdSizeValue || 0;
			} else {
				sizeValue = 0;
			}

			// here we need to support changing what value is used for the size property
			const existing = existingNodes[label];
			if (existing) {
				existing.sizeValue = sizeValue;
				nodes[label] = existing;
			} else {
				nodes[label] = { label, sizeValue: sizeValue };
			}
		});

		// handle color value settings
		if (widgetConfiguration?.colorScale === DefaultChartColorScales.Sequential) {
			// sequential scale uses only the index
			getColorScale.domain([0, data.nodes.length || 0]);
			data.nodes.forEach((d, index) => {
				nodes[d.label].colorValue = index;
			});
		} else {
			// need to acquire the keys and there are two choices, either the key itself or a metadata value
			getColorScale.domain(data.nodes.map((d) => d.label));
			data.nodes.forEach((d) => {
				nodes[d.label].colorValue = d.label;
			});
		}

		// for each edge, create a link to the nodes
		data.edges.forEach(({ node1, node2 }) => {
			const key = `${node1}^${node2}`;
			if (nodes[node1] && nodes[node2]) {
				links[key] = { source: nodes[node1], target: nodes[node2], key: key };
			}
		});

		// poorly done implementation to update what exists and remove what doesn't
		nodeDom.current
			.selectAll('div')
			.data(Object.values(nodes), (d: Node) => d.label)
			.join(
				(enter) => enter.append('div').text((d: Node) => d.label),
				(update) => update,
				(exit) => exit.remove()
			);

		linkDom.current
			.selectAll('div')
			.data(Object.values(links), (d: Link) => d.key)
			.join(
				(enter) => enter.append('div').text((d: Link) => d.key),
				(update) => update,
				(exit) => exit.remove()
			);

		setConvertedData({
			nodes: nodeDom.current.selectAll('div').data(),
			links: linkDom.current.selectAll('div').data(),
		});
	}, [data, widgetConfiguration, endpointQuery, getColorScale]);

	return convertedData?.nodes?.length > 0 ? (
		<NodeLink
			data={convertedData}
			dimensions={dimensions}
			getColor={getColorScale}
			widgetConfiguration={widgetConfiguration}
		/>
	) : (
		<NonIdealState
			title={'No Clusters Found'}
			icon={'graph-remove'}
			description={'No clusters were found with the given parameters.  Try making another selection'}
		/>
	);
}
